import { useEffect, useState } from 'react';
import { initBugTracker, ADMIN_BUGTRACKER_KEY } from './bugtracker';
import { Wordmark } from './brand';
import { useTheme } from './theme';
import { useHashView } from './useHashView';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { Ico, ICON } from './icons';
import {
  OverviewView, TenantsView, OrgDetailView,
  SyncHealthView, PlansView, AdminsView, AuditView,
} from './views';
import { FeaturesView } from './features';
import { PlatformIntegrationsView } from './platform-integrations';
import { featureRequestCount, markFeatureRequestsSeen, platformRoleCatalog, type PlatformCapability } from './api';

type View =
  | { name: 'overview' }
  | { name: 'tenants' }
  | { name: 'org'; orgId: string }
  | { name: 'sync' }
  | { name: 'plans' }
  | { name: 'admins' }
  | { name: 'audit' }
  | { name: 'features' }
  | { name: 'integrations' };

const TITLES: Record<View['name'], string> = {
  overview: 'Overview',
  tenants: 'Tenants',
  org: 'Tenant',
  sync: 'Sync Health',
  plans: 'Plans',
  admins: 'Platform Admins',
  audit: 'Audit Log',
  features: 'Feature Requests',
  integrations: 'Integrations',
};

/**
 * Platform admin shell.
 *
 * Same structure as the Conduit admin portal — fixed left sidebar with icon
 * nav, sticky topbar carrying the section title and the signed-in user, version
 * pinned in the sidebar footer. Kept on our own palette rather than Conduit's.
 *
 * Deliberately NOT the tenant dashboard's layout: this is the control plane and
 * should never be mistaken for the product at a glance.
 */
export function App({ email }: { email: string | null }) {
  // Same reason as the tenant sidebar: the mount node must exist before init.
  useEffect(() => {
    if (!email) return;
    initBugTracker(ADMIN_BUGTRACKER_KEY, { uid: email, email }, __APP_VERSION__);
  }, [email]);

  // Org detail carries an id, so route on the NAME and keep the id in state —
  // a refresh lands you on Tenants rather than a blank tenant, which is the
  // honest fallback when the id is not in the URL.
  const [routeName, setRouteName] = useHashView<View['name']>(
    ['overview', 'tenants', 'org', 'sync', 'plans', 'admins', 'audit', 'features', 'integrations'], 'overview');
  const [theme, toggleTheme] = useTheme();
  const [orgId, setOrgId] = useState<string | null>(null);

  /**
   * What this administrator may do.
   *
   * Asked of the server rather than read from the token: the token is the same
   * source, but going through the callable means the console and the guard
   * agree by construction instead of by two people remembering the same rule.
   *
   * Until it answers, nothing privileged is shown. An empty sidebar for a
   * moment is better than a Plans link that vanishes once the answer arrives.
   */
  const [caps, setCaps] = useState<PlatformCapability[] | null>(null);
  useEffect(() => {
    let live = true;
    platformRoleCatalog({})
      .then((c) => { if (live) setCaps(c.myCapabilities); })
      // A failure here must not blank the console for somebody who can in fact
      // use it, so fall back to the read-only floor.
      .catch(() => { if (live) setCaps(['tenants.view']); });
    return () => { live = false; };
  }, []);
  const can = (c: PlatformCapability) => (caps ?? []).includes(c);

  // "Arrived since you last looked", not "untriaged" — see features.tsx.
  const [newFeatures, setNewFeatures] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => {
      featureRequestCount({})
        .then((r) => { if (alive) setNewFeatures(r.newCount); })
        .catch(() => { /* a badge is not worth an error banner */ });
    };
    tick();
    const id = setInterval(tick, 120_000);
    // The queue clears its own badge the moment it is opened, without waiting
    // for the next poll.
    const onSeen = () => setNewFeatures(0);
    window.addEventListener('features-seen', onSeen);
    return () => { alive = false; clearInterval(id); window.removeEventListener('features-seen', onSeen); };
  }, []);
  const view: View = routeName === 'org' && orgId
    ? { name: 'org', orgId }
    : ({ name: routeName === 'org' ? 'tenants' : routeName } as View);
  const setView = (v: View) => {
    if (v.name === 'org') { setOrgId(v.orgId); setRouteName('org'); }
    else { setRouteName(v.name); }
  };

  const navBtn = (name: View['name'], label: string, icon: string, badge = 0) => (
    <button
      className={
        'nav-item' +
        // Drilling into a tenant keeps Tenants lit, so you never lose your place.
        ((view.name === name || (name === 'tenants' && view.name === 'org')) ? ' active' : '')
      }
      onClick={() => setView({ name } as View)}
    >
      <Ico d={icon} />
      <span className="nav-label">{label}</span>
      {badge > 0 && (
        <span className="nav-badge" title={`${badge} new since you last looked`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="side-brand"><Wordmark width={168} /></div>
        <div className="side-sub">Platform Admin</div>

        <nav className="nav">
          <div className="nav-group-label">Platform</div>
          {navBtn('overview', 'Overview', ICON.overview)}
          {navBtn('tenants', 'Tenants', ICON.tenants)}

          <div className="nav-group-label">Operations</div>
          {navBtn('sync', 'Sync Health', ICON.sync)}
          {can('plans.manage') && navBtn('plans', 'Plans', ICON.plans)}
          {can('features.manage') && navBtn('features', 'Feature Requests', ICON.requests, newFeatures)}
          {can('integrations.manage') && navBtn('integrations', 'Integrations', ICON.plug)}

          <div className="nav-group-label">Access</div>
          {navBtn('admins', 'Platform Admins', ICON.admins)}
          {can('audit.view') && navBtn('audit', 'Audit Log', ICON.audit)}
        </nav>

        <div className="util-row">
          <button
            className="util-btn" onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            aria-label="Toggle theme"
          >
            <Ico d={theme === 'dark' ? ICON.sun : ICON.moon} />
          </button>
          <span className="req-wrap">
            <button
              className={'util-btn req-bell' + (newFeatures > 0 ? ' has-new' : '')}
              onClick={() => setView({ name: 'features' })}
              title={newFeatures > 0
                ? `${newFeatures} new feature request${newFeatures === 1 ? '' : 's'} — click to open`
                : 'No new feature requests'}
              aria-label="Feature requests"
            >
              <Ico d={ICON.requests} />
              {newFeatures > 0 && (
                <span className="req-count">{newFeatures > 99 ? '99+' : newFeatures}</span>
              )}
            </button>
            {/* Dismiss without opening. Someone who already knows what is in
                there should not have to navigate away from what they are doing
                just to clear a number. */}
            {newFeatures > 0 && (
              <button
                className="req-dismiss"
                title="Dismiss — mark these as seen without opening"
                onClick={() => {
                  void markFeatureRequestsSeen({ at: Date.now() })
                    .then(() => setNewFeatures(0))
                    .catch(() => { /* non-fatal */ });
                }}
              >
                &times;
              </button>
            )}
          </span>
          <div id="bugtracker-mount" className="util-mount" title="Report a bug" />
        </div>

        <div className="side-foot" title={`Build ${__BUILD_ID__}\nBuilt ${__BUILT_AT__}`}>
          v{__APP_VERSION__}
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <h2>{TITLES[view.name]}</h2>
          <div className="user">
            <span className="mono dim">{email}</span>
            <span className="dim">·</span>
            <button className="btn-link" onClick={() => void signOut(auth)}>Sign out</button>
          </div>
        </div>

        <div className="content">
          <div className="fade" key={view.name + ('orgId' in view ? view.orgId : '')}>
            {view.name === 'overview' && <OverviewView onOpen={(orgId) => setView({ name: 'org', orgId })} />}
            {view.name === 'tenants' && <TenantsView onOpen={(orgId) => setView({ name: 'org', orgId })} />}
            {view.name === 'org' && <OrgDetailView orgId={view.orgId} onBack={() => setView({ name: 'tenants' })} />}
            {view.name === 'sync' && <SyncHealthView />}
            {view.name === 'plans' && <PlansView />}
            {view.name === 'admins' && <AdminsView selfEmail={email ?? ''} />}
            {view.name === 'audit' && <AuditView />}
            {view.name === 'features' && <FeaturesView />}
            {view.name === 'integrations' && <PlatformIntegrationsView />}
          </div>
        </div>
      </div>
    </div>
  );
}
